import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import * as pdfjsLib from 'pdfjs-dist';
import {
  ChevronLeft, ChevronRight, ZoomIn, ZoomOut, Loader2, Wand2, Eraser,
  MousePointer2, Heading, AlertTriangle, Check,
} from 'lucide-react';
import { Button } from '../Button';
import { extractPdfPages, hasTextLayer, PdfPageText } from '../../services/pdf/pdfTextExtract';
import { groupPageLines, PdfTextLine } from '../../services/pdf/pdfTextLines';
import {
  ColumnRole, COLUMN_ROLE_LABELS, detectColumnBands, guessColumnRoles,
} from '../../services/pdf/lvColumnLayout';
import {
  LineMark, LvMarkingState, MarkedLvRow, autoMarkPositions, clearMarks, emptyMarkingState,
  hasRowSplitter, rowsFromMarking, setBandRole, setMarks, summarizeMarking, toggleMark,
} from '../../services/pdf/lvManualMarking';

/**
 * Markiermodus: der Nutzer bestimmt die LV-Positionen selbst im Original-PDF.
 *
 * Duenner Container. Alles, was aus Markierungen Zeilen macht — Zeilenbildung,
 * Spaltenraster, Auswahl, Zusammenfuehrung mehrzeiliger Texte — liegt in
 * services/pdf/* und ist ohne DOM testbar. Hier steht nur Rendering,
 * Koordinatenumrechnung und Eventweitergabe.
 */

interface Props {
  file: File;
  /** Uebernahme: dieselbe Zeilenform, die auch die KI-Extraktion liefert. */
  onApply: (rows: MarkedLvRow[]) => void;
  onCancel: () => void;
  /** Kein Textlayer (reiner Scan) — der Aufrufer bietet dann den KI-Pfad an. */
  onNoTextLayer: () => void;
}

type Tool = LineMark | 'erase';

const TOOLS: { value: Tool; label: string; icon: React.ReactNode; hint: string }[] = [
  { value: 'position', label: 'Position', icon: <MousePointer2 className="w-3.5 h-3.5" />, hint: 'Zeilen als Leistungsposition markieren' },
  { value: 'heading', label: 'Titel', icon: <Heading className="w-3.5 h-3.5" />, hint: 'Zeile als Abschnitts-/Titelzeile markieren' },
  { value: 'erase', label: 'Entfernen', icon: <Eraser className="w-3.5 h-3.5" />, hint: 'Markierung wieder aufheben' },
];

const ROLE_OPTIONS: ColumnRole[] = ['ignore', 'pos', 'bezeichnung', 'menge', 'einheit'];

export const LvMarkupView: React.FC<Props> = ({ file, onApply, onCancel, onNoTextLayer }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const renderTaskRef = useRef<{ cancel: () => void; promise: Promise<void> } | null>(null);
  const docRef = useRef<any>(null);
  /**
   * Der Ausstieg fuer scan-only PDFs, als Ref gehalten.
   *
   * Als Effekt-Abhaengigkeit war er der teuerste Fehler des Modus: der
   * Aufrufer uebergibt eine Inline-Funktion, deren Identitaet sich bei JEDEM
   * Rendern des Angebotseditors aendert — und der Autosave rendert alle zwei
   * Sekunden. Der Lade-Effekt lief dadurch mitten im Markieren erneut und
   * setzte den Markierungszustand auf die Auto-Vorauswahl zurueck. Bei einem
   * 120-Positionen-LV war die gesamte Handarbeit weg, ohne Meldung.
   */
  const noTextLayerRef = useRef(onNoTextLayer);
  useEffect(() => { noTextLayerRef.current = onNoTextLayer; }, [onNoTextLayer]);
  /** Ein Zug hat gerade markiert — der folgende Klick darf ihn nicht aufheben. */
  const draggedRef = useRef(false);
  /** Die zuletzt automatisch erzeugte Markierung — Vergleich fuer die Rueckfrage. */
  const autoMarksRef = useRef<LvMarkingState['marks'] | null>(null);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [pages, setPages] = useState<PdfPageText[]>([]);
  const [pageIndex, setPageIndex] = useState(0);
  const [scale, setScale] = useState(1.3);
  const [tool, setTool] = useState<Tool>('position');
  const [marking, setMarking] = useState<LvMarkingState>(emptyMarkingState());
  const [drag, setDrag] = useState<{ x: number; y: number; cx: number; cy: number } | null>(null);
  /**
   * Der Viewport traegt die Umrechnung PDF <-> Bildschirm. Bewusst State und
   * nicht Ref: die Baender werden daraus GERENDERT. Als Ref gelesen wuerde die
   * Ueberlagerung nach einem Zoom auf den alten Koordinaten stehen bleiben,
   * bis irgendetwas anderes ein Neuzeichnen ausloest.
   */
  const [viewport, setViewport] = useState<any>(null);

  // ─── Laden: Textebene einmal, Renderdokument getrennt ──────────────────────
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        setLoading(true);
        const buf = new Uint8Array(await file.arrayBuffer());
        const extracted = await extractPdfPages(buf);
        if (!alive) return;
        if (!hasTextLayer(extracted)) {
          noTextLayerRef.current();
          return;
        }
        // Eigene Kopie fuers Rendern: extractPdfPages hat sein Dokument bereits
        // zerstoert und pdfjs haelt den Puffer nicht.
        const doc = await pdfjsLib.getDocument({ data: buf.slice() }).promise;
        if (!alive) { await doc.destroy(); return; }
        docRef.current = doc;

        // Spaltenraster und Vorauswahl gleich hier ableiten, nicht in einem
        // Folge-Effekt: das Raster haengt nur am Dokument, und ein zweiter
        // Renderdurchlauf nur zum Setzen des Anfangszustands ist unnoetig.
        const lines = extracted.map((pg, i) => groupPageLines(pg.items, i)).flat();
        const bands = guessColumnRoles(detectColumnBands(lines), lines);
        const initial = autoMarkPositions(emptyMarkingState(bands), lines);
        autoMarksRef.current = initial.marks;

        setPages(extracted);
        setMarking(initial);
        setLoading(false);
      } catch (e) {
        if (!alive) return;
        setError((e as Error)?.message || 'Die PDF konnte nicht gelesen werden.');
        setLoading(false);
      }
    })();
    return () => {
      alive = false;
      const d = docRef.current;
      docRef.current = null;
      if (d) d.destroy();
    };
  }, [file]);

  // Zeilen je Seite. Bewusst am Dokument haengend und nicht am Zoom: die
  // Gruppierung rechnet in PDF-Koordinaten, die Skalierung beruehrt sie nicht.
  // Das Spaltenraster daraus entsteht einmalig beim Laden (siehe oben).
  const linesByPage = useMemo(
    () => pages.map((p, i) => groupPageLines(p.items, i)),
    [pages],
  );
  const allLines = useMemo(() => linesByPage.flat(), [linesByPage]);
  const pageLines = linesByPage[pageIndex] ?? [];

  // ─── Rendern ───────────────────────────────────────────────────────────────
  useEffect(() => {
    const doc = docRef.current;
    const canvas = canvasRef.current;
    if (!doc || !canvas || loading) return;
    let cancelled = false;

    (async () => {
      try {
        const page = await doc.getPage(pageIndex + 1);
        if (cancelled) return;
        const viewport = page.getViewport({ scale });
        // Kein devicePixelRatio: Canvas-Pixel == CSS-Pixel haelt die
        // Overlay-Mathematik 1:1 — dieselbe Entscheidung wie im PdfCropper.
        canvas.width = viewport.width;
        canvas.height = viewport.height;
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;

        if (renderTaskRef.current) renderTaskRef.current.cancel();
        const ctx = canvas.getContext('2d');
        if (!ctx) return;
        const task = page.render({ canvasContext: ctx, viewport });
        renderTaskRef.current = task as any;
        await task.promise;
        if (!cancelled) setViewport(viewport);
      } catch (e) {
        // Ein abgebrochener Renderlauf ist der Normalfall beim Zoomen.
        if ((e as any)?.name !== 'RenderingCancelledException' && !cancelled) {
          console.error('LV-Markierung: Seite konnte nicht gerendert werden.', e);
        }
      }
    })();

    return () => { cancelled = true; };
  }, [pageIndex, scale, loading, pages.length]);

  // ─── Koordinaten ───────────────────────────────────────────────────────────
  /** PDF-Punkt -> Bildschirmpunkt im Canvas. */
  const toScreen = useCallback((x: number, y: number): [number, number] => {
    if (!viewport) return [0, 0];
    const [sx, sy] = viewport.convertToViewportPoint(x, y);
    return [sx, sy];
  }, [viewport]);

  /** Mausposition im Overlay -> PDF-Punkt. */
  const toPdf = useCallback((clientX: number, clientY: number): { x: number; y: number } => {
    const el = overlayRef.current;
    if (!viewport || !el) return { x: 0, y: 0 };
    const r = el.getBoundingClientRect();
    const [x, y] = viewport.convertToPdfPoint(clientX - r.left, clientY - r.top);
    return { x, y };
  }, [viewport]);

  /** Bildschirmrechteck einer Textzeile. */
  const lineBox = useCallback((l: PdfTextLine) => {
    const [ax, ay] = toScreen(l.x0, l.y + l.size);
    const [bx, by] = toScreen(l.x1, l.y - l.size * 0.3);
    return {
      left: Math.min(ax, bx),
      top: Math.min(ay, by),
      width: Math.max(2, Math.abs(bx - ax)),
      height: Math.max(6, Math.abs(by - ay)),
    };
  }, [toScreen]);

  // ─── Interaktion ───────────────────────────────────────────────────────────
  const onMouseDown = (e: React.MouseEvent) => {
    if (e.button !== 0) return;
    draggedRef.current = false;
    const p = toPdf(e.clientX, e.clientY);
    const r = overlayRef.current!.getBoundingClientRect();
    setDrag({ x: p.x, y: p.y, cx: e.clientX - r.left, cy: e.clientY - r.top });
  };

  const [dragNow, setDragNow] = useState<{ x: number; y: number; cx: number; cy: number } | null>(null);
  const onMouseMove = (e: React.MouseEvent) => {
    if (!drag) return;
    const p = toPdf(e.clientX, e.clientY);
    const r = overlayRef.current!.getBoundingClientRect();
    setDragNow({ x: p.x, y: p.y, cx: e.clientX - r.left, cy: e.clientY - r.top });
  };

  const onMouseUp = (e: React.MouseEvent) => {
    if (!drag) return;
    const r = overlayRef.current!.getBoundingClientRect();
    const cy = e.clientY - r.top;
    const cx = e.clientX - r.left;
    const moved = Math.abs(cy - drag.cy) > 2 || Math.abs(cx - drag.cx) > 2;
    if (moved) {
      // Bewusst in BILDSCHIRM-Koordinaten und nicht im PDF-Raum: bei einer
      // Seite mit /Rotate 90 entspricht "nach unten ziehen" einer Bewegung
      // entlang der PDF-x-Achse. Eine Y-Pruefung im PDF-Raum traefe dort nur
      // die eine Zeile auf genau dieser Hoehe — die Rechteckauswahl waere tot.
      const top = Math.min(cy, drag.cy);
      const bottom = Math.max(cy, drag.cy);
      const hits = pageLines
        .filter((l) => { const b = lineBox(l); return b.top <= bottom && b.top + b.height >= top; })
        .map((l) => l.id);
      setMarking((m) => setMarks(m, hits, tool === 'erase' ? null : tool));
      draggedRef.current = true;
    }
    setDrag(null);
    setDragNow(null);
  };

  const onLineClick = (id: string) => {
    // Nach einem Zug feuert der Browser zusaetzlich ein click auf das Band,
    // auf dem er endete — ohne diese Sperre haette der Zug sich selbst wieder
    // aufgehoben und schiene wirkungslos. Zurueckgesetzt wird sie beim
    // naechsten mousedown, nicht hier: endet der Zug im Weissraum, kommt gar
    // kein Klick auf ein Band, und die Sperre wuerde sonst haengen bleiben.
    if (draggedRef.current) return;
    if (tool === 'erase') {
      setMarking((m) => setMarks(m, [id], null));
      return;
    }
    setMarking((m) => toggleMark(m, id, tool));
  };

  // ─── Abgeleitetes ──────────────────────────────────────────────────────────
  const summary = useMemo(() => summarizeMarking(allLines, marking), [allLines, marking]);
  const rows = useMemo(() => rowsFromMarking(allLines, marking), [allLines, marking]);
  const bands = marking.bands;
  const pageWidth = pages[pageIndex]?.width ?? 595;

  const dragBox = drag && dragNow ? {
    left: Math.min(drag.cx, dragNow.cx),
    top: Math.min(drag.cy, dragNow.cy),
    width: Math.abs(dragNow.cx - drag.cx),
    height: Math.abs(dragNow.cy - drag.cy),
  } : null;

  if (error) {
    return (
      <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
        <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
        <span>{error}</span>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-slate-500">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        <span className="text-sm font-semibold">PDF wird gelesen…</span>
        <span className="text-xs">Textebene und Spaltenraster werden bestimmt.</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      {/* Werkzeugleiste */}
      <div className="flex flex-wrap items-center gap-2 p-2 bg-slate-50 border border-slate-200 rounded-xl">
        <div className="flex items-center gap-0.5 bg-white border border-slate-200 rounded-lg p-0.5">
          {TOOLS.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setTool(t.value)}
              title={t.hint}
              className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-semibold transition-all ${
                tool === t.value ? 'bg-blue-600 text-white' : 'text-slate-500 hover:bg-slate-100'
              }`}
            >
              {t.icon}{t.label}
            </button>
          ))}
        </div>

        <button
          type="button"
          onClick={() => {
            // Der Lauf ersetzt die Markierung vollstaendig. Solange nur die
            // eigene Vorauswahl dasteht, ist das folgenlos — nach Handarbeit
            // waere es stiller Verlust. Die Rueckfrage steht bewusst VOR dem
            // State-Updater: unter React.StrictMode laeuft der doppelt.
            const touched = Object.keys(marking.marks).length > 0 && marking.marks !== autoMarksRef.current;
            if (touched && !window.confirm('Die vorhandenen Markierungen werden dabei ersetzt. Fortfahren?')) return;
            const next = autoMarkPositions(marking, allLines);
            autoMarksRef.current = next.marks;
            setMarking(next);
          }}
          className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 transition-all"
          title="Alle Zeilen mit einer Nummer in der LV-Spalte markieren — inklusive ihrer Fortsetzungszeilen"
        >
          <Wand2 className="w-3.5 h-3.5" />Automatisch markieren
        </button>
        <button
          type="button"
          onClick={() => setMarking((m) => clearMarks(m, pageIndex))}
          className="px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-white border border-slate-200 text-slate-600 hover:bg-slate-100 transition-all"
        >
          Seite leeren
        </button>

        <div className="flex-1" />

        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setScale((s) => Math.max(0.5, +(s - 0.2).toFixed(2)))} className="p-1.5 rounded-lg text-slate-500 hover:bg-white transition-all" title="Verkleinern">
            <ZoomOut className="w-4 h-4" />
          </button>
          <span className="text-xs font-semibold text-slate-500 w-10 text-center">{Math.round(scale * 100)}%</span>
          <button type="button" onClick={() => setScale((s) => Math.min(3, +(s + 0.2).toFixed(2)))} className="p-1.5 rounded-lg text-slate-500 hover:bg-white transition-all" title="Vergrößern">
            <ZoomIn className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center gap-1">
          <button type="button" onClick={() => setPageIndex((i) => Math.max(0, i - 1))} disabled={pageIndex === 0} className="p-1.5 rounded-lg text-slate-500 hover:bg-white disabled:opacity-30 transition-all">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-xs font-semibold text-slate-600">Seite {pageIndex + 1} / {pages.length}</span>
          <button type="button" onClick={() => setPageIndex((i) => Math.min(pages.length - 1, i + 1))} disabled={pageIndex >= pages.length - 1} className="p-1.5 rounded-lg text-slate-500 hover:bg-white disabled:opacity-30 transition-all">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Spaltenrollen — einmal gesetzt, gilt fuer alle Seiten */}
      <div className="flex flex-wrap items-center gap-2 px-1">
        <span className="text-[11px] uppercase font-bold text-slate-400 tracking-wider">Spalten</span>
        {bands.length === 0 && <span className="text-xs text-slate-400">Kein Spaltenraster erkannt — jede markierte Zeile wird als eigene Position mit ihrem vollen Text übernommen.</span>}
        {!hasRowSplitter(marking) && (
          <span className="flex items-center gap-1.5 text-xs font-semibold text-amber-700">
            <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
            Keine LV-Nummern-, Mengen- oder Einheiten-Spalte zugeordnet — jede markierte Zeile wird eine
            eigene Position. Mehrzeilige Leistungstexte bleiben dann getrennt und sind im nächsten Schritt
            zusammenzuführen.
          </span>
        )}
        {bands.map((b, i) => (
          <label key={i} className="flex items-center gap-1 text-xs">
            <span className="text-slate-400">{i + 1}</span>
            <select
              value={b.role}
              onChange={(e) => setMarking((m) => setBandRole(m, i, e.target.value as ColumnRole))}
              className={`px-1.5 py-1 bg-white border rounded-lg text-xs outline-none focus:ring-2 focus:ring-blue-500/20 ${
                b.role === 'ignore' ? 'border-slate-200 text-slate-400' : 'border-blue-200 text-blue-700 font-semibold'
              }`}
            >
              {ROLE_OPTIONS.map((r) => <option key={r} value={r}>{COLUMN_ROLE_LABELS[r]}</option>)}
            </select>
          </label>
        ))}
      </div>

      {/* Seite */}
      <div className="relative overflow-auto max-h-[52vh] bg-slate-100 rounded-xl border border-slate-200 p-3">
        <div className="relative inline-block shadow-lg" ref={overlayRef}
          onMouseDown={onMouseDown} onMouseMove={onMouseMove} onMouseUp={onMouseUp} onMouseLeave={() => { setDrag(null); setDragNow(null); }}
        >
          <canvas ref={canvasRef} className="block bg-white select-none" />

          {/* Spaltenkanten als schwache Hilfslinien */}
          {viewport && bands.slice(1).map((b, i) => {
            const [x] = toScreen(Math.max(0, Math.min(pageWidth, b.x0)), 0);
            return <div key={`band-${i}`} className="absolute top-0 bottom-0 border-l border-dashed border-blue-300/50 pointer-events-none" style={{ left: x }} />;
          })}

          {/* Textzeilen als anklickbare Baender */}
          {viewport && pageLines.map((l) => {
            const box = lineBox(l);
            const mark = marking.marks[l.id];
            const cls = mark === 'position'
              ? 'bg-blue-500/25 border-blue-500'
              : mark === 'heading'
                ? 'bg-emerald-500/25 border-emerald-500'
                : 'border-transparent hover:bg-blue-400/10 hover:border-blue-300';
            return (
              <div
                key={l.id}
                onClick={(e) => { e.stopPropagation(); onLineClick(l.id); }}
                title={l.text}
                className={`absolute border rounded-[3px] cursor-pointer transition-colors ${cls}`}
                style={{ left: box.left, top: box.top, width: box.width, height: box.height }}
              />
            );
          })}

          {/* Ziehrechteck */}
          {dragBox && dragBox.height > 2 && (
            <div className="absolute border-2 border-blue-500 bg-blue-500/10 pointer-events-none rounded"
              style={{ left: dragBox.left, top: dragBox.top, width: dragBox.width, height: dragBox.height }} />
          )}
        </div>
      </div>

      {/* Bilanz + Uebernahme */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-slate-500">
          <span><strong className="text-slate-700">{summary.positions}</strong> Positionen</span>
          <span><strong className="text-slate-700">{summary.headings}</strong> Titel</span>
          {summary.withoutQuantity > 0 && <span className="text-amber-600">{summary.withoutQuantity} ohne Menge</span>}
          {summary.withoutNumber > 0 && <span className="text-amber-600">{summary.withoutNumber} ohne LV-Nummer</span>}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={onCancel}>Abbrechen</Button>
          <Button onClick={() => onApply(rows)} disabled={!rows.length} icon={<Check className="w-4 h-4" />}>
            {rows.length} Zeile{rows.length === 1 ? '' : 'n'} übernehmen
          </Button>
        </div>
      </div>

      <p className="text-[11px] text-slate-400 px-1">
        Ziehen markiert alle berührten Zeilen, Klick schaltet eine einzelne um. Zeilen ohne eigene LV-Nummer
        gehören zur Position darüber und werden an deren Text angehängt — so bleiben mehrzeilige Leistungstexte
        zusammen. Danach folgt das gewohnte Review, in dem die KI die markierten Zeilen nur noch in Felder ausrichtet.
      </p>
    </div>
  );
};
